import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getMyProducts, deleteProduct, updateProduct } from '@/lib/api';
import { getFirstImage } from '@/utils/imageUtils';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye,
  Package,
  ImageIcon,
  Upload,
  Star,
  StarOff,
  FileSpreadsheet,
  Download,
  HelpCircle,
  ZoomIn,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Image Modal Component
const ImageModal = ({ isOpen, onClose, imageSrc, title }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh] w-full">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
        >
          <X className="w-8 h-8" />
        </button>
        {title && (
          <p className="text-white text-center mb-2 text-lg font-medium">{title}</p>
        )}
        <img 
          src={imageSrc} 
          alt={title || 'Enlarged view'}
          className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>,
    document.body
  );
};

const ProductList = () => {
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [imageModal, setImageModal] = useState({ isOpen: false, src: '', title: '' });
  
  const isPro = user?.subscription_plan === 'pro' || user?.subscription_plan === 'sponsor' || user?.role === 'admin';
  const featuredCount = products.filter(p => p.is_featured).length;

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products/template/excel`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Error al descargar');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_catalogo.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isUSA ? 'Template downloaded' : 'Plantilla descargada');
    } catch (error) {
      toast.error(isUSA ? 'Error downloading template' : 'Error al descargar plantilla');
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!isPro) {
      toast.error(isUSA ? 'PRO feature: Upgrade to import via Excel' : 'Función PRO: Actualiza para importar por Excel');
      return;
    }
    
    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error(isUSA ? 'Please select an Excel file (.xlsx)' : 'Selecciona un archivo Excel (.xlsx)');
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/products/import/excel`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error importing');
      
      // Show detailed message
      if (data.results?.errors?.length > 0) {
        toast.warning(`${data.message}. Errores: ${data.results.errors.slice(0, 3).join(', ')}${data.results.errors.length > 3 ? '...' : ''}`, { duration: 8000 });
      } else {
        toast.success(data.message);
      }
      
      fetchProducts(); // Refresh list
    } catch (error) {
      toast.error(error.message || (isUSA ? 'Error importing' : 'Error al importar'));
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset input
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await getMyProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm(isUSA ? 'Are you sure you want to delete this product?' : '¿Estás seguro de eliminar este producto?')) return;
    
    try {
      await deleteProduct(productId);
      setProducts(products.filter(p => p.product_id !== productId));
      toast.success(isUSA ? 'Product deleted' : 'Producto eliminado');
    } catch (error) {
      toast.error(isUSA ? 'Error deleting product' : 'Error al eliminar producto');
    }
  };

  const handleToggleFeatured = async (productId, currentValue) => {
    const newValue = !currentValue;
    
    // Check if trying to feature and already at limit
    if (newValue && featuredCount >= 3) {
      toast.error(isUSA 
        ? 'You can only feature up to 3 products. Remove one first.' 
        : 'Solo puedes destacar hasta 3 productos. Quita uno primero.');
      return;
    }
    
    try {
      await updateProduct(productId, { is_featured: newValue });
      setProducts(products.map(p => 
        p.product_id === productId ? { ...p, is_featured: newValue } : p
      ));
      toast.success(newValue 
        ? (isUSA ? 'Product featured!' : '¡Producto destacado!') 
        : (isUSA ? 'Product unfeatured' : 'Producto quitado de destacados'));
    } catch (error) {
      toast.error(error.message || (isUSA ? 'Error updating product' : 'Error al actualizar producto'));
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">{isUSA ? 'My Products' : 'Mis Productos'}</h1>
          <p className="page-subtitle">{products.length} {isUSA ? 'products in your catalog' : 'productos en tu catálogo'}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadTemplate} title={isUSA ? 'Download Excel template' : 'Descargar plantilla Excel'}>
            <Download className="mr-2 w-4 h-4" />
            {isUSA ? 'Template' : 'Plantilla'}
          </Button>
          <label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
              className="hidden"
              disabled={importing || !isPro}
            />
            <Button 
              variant="outline" 
              disabled={importing || !isPro}
              title={isPro ? (isUSA ? 'Import from Excel' : 'Importar desde Excel') : (isUSA ? 'PRO feature' : 'Función PRO')}
              asChild
            >
              <span className="cursor-pointer">
                <FileSpreadsheet className="mr-2 w-4 h-4" />
                {importing ? (isUSA ? 'Importing...' : 'Importando...') : (isUSA ? 'Import' : 'Importar')}
              </span>
            </Button>
          </label>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/help/excel')}
            title={isUSA ? 'Excel Tutorial' : 'Tutorial Excel'}
          >
            <HelpCircle className="w-4 h-4" />
          </Button>
          <Link to="/dashboard/products/new">
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-product-btn">
              <Plus className="mr-2 w-4 h-4" />
              {isUSA ? 'Add product' : 'Agregar producto'}
            </Button>
          </Link>
        </div>
      </div>

      {/* Featured Products Counter - PRO only */}
      {isPro && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Star className="w-5 h-5 text-amber-500" />
            <div>
              <p className="font-medium text-amber-900">
                {isUSA ? 'Featured Products' : 'Productos Destacados'}
              </p>
              <p className="text-sm text-amber-700">
                {isUSA 
                  ? `${featuredCount}/3 slots used. Featured products appear at the top of the catalog.`
                  : `${featuredCount}/3 espacios usados. Los productos destacados aparecen al inicio del catálogo.`}
              </p>
            </div>
          </div>
          <Badge className="bg-amber-500 text-white">{featuredCount}/3</Badge>
        </div>
      )}

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={isUSA ? 'Search products...' : 'Buscar productos...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-products-input"
          />
        </div>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="empty-state">
          <Package className="empty-state-icon" />
          <h3 className="empty-state-title">
            {products.length === 0 
              ? (isUSA ? 'No products yet' : 'No tienes productos') 
              : (isUSA ? 'No products found' : 'No se encontraron productos')}
          </h3>
          <p className="empty-state-text">
            {products.length === 0 
              ? (isUSA ? 'Start by adding your first product to the catalog' : 'Comienza agregando tu primer producto al catálogo')
              : (isUSA ? 'Try different search terms' : 'Intenta con otros términos de búsqueda')}
          </p>
          {products.length === 0 && (
            <Link to="/dashboard/products/new">
              <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                <Plus className="mr-2 w-4 h-4" />
                {isUSA ? 'Add product' : 'Agregar producto'}
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <Card 
              key={product.product_id} 
              className={`product-card ${product.is_featured ? 'ring-2 ring-amber-400' : ''}`} 
              data-testid={`product-card-${product.product_id}`}
            >
              <div className="relative">
                {/* Featured badge */}
                {product.is_featured && (
                  <div className="absolute top-2 left-2 z-10">
                    <Badge className="bg-amber-500 text-white">
                      <Star className="w-3 h-3 mr-1" />
                      {isUSA ? 'Featured' : 'Destacado'}
                    </Badge>
                  </div>
                )}
                {product.images && product.images.length > 0 ? (
                  <div 
                    className="relative cursor-pointer group"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setImageModal({
                        isOpen: true,
                        src: getFirstImage(product.images),
                        title: product.name
                      });
                    }}
                  >
                    <img 
                      src={getFirstImage(product.images)} 
                      alt={product.name}
                      className="product-card-image"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ) : (
                  <div className="product-card-image flex flex-col items-center justify-center bg-slate-50">
                    <ImageIcon className="w-10 h-10 text-slate-300 mb-1" />
                    <span className="text-xs text-slate-400">{isUSA ? 'No image' : 'Sin imagen'}</span>
                  </div>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isPro && (
                      <>
                        <DropdownMenuItem 
                          onClick={() => handleToggleFeatured(product.product_id, product.is_featured)}
                          className={product.is_featured ? 'text-amber-600' : ''}
                        >
                          {product.is_featured ? (
                            <>
                              <StarOff className="mr-2 w-4 h-4" />
                              {isUSA ? 'Remove from featured' : 'Quitar de destacados'}
                            </>
                          ) : (
                            <>
                              <Star className="mr-2 w-4 h-4" />
                              {isUSA ? 'Feature product' : 'Destacar producto'}
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => navigate(`/dashboard/products/${product.product_id}/edit`)}>
                      <Edit className="mr-2 w-4 h-4" />
                      {isUSA ? 'Edit' : 'Editar'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/products/${product.product_id}`)}>
                      <Eye className="mr-2 w-4 h-4" />
                      {isUSA ? 'View public' : 'Ver público'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(product.product_id)}
                      className="text-red-600"
                    >
                      <Trash2 className="mr-2 w-4 h-4" />
                      {isUSA ? 'Delete' : 'Eliminar'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <CardContent className="product-card-content">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-slate-900 line-clamp-1">{product.name}</h3>
                    <p className="text-sm text-slate-500">{product.category}</p>
                  </div>
                  <Badge 
                    variant="outline"
                    className={product.availability === 'disponible' 
                      ? 'text-emerald-600 border-emerald-200' 
                      : 'text-slate-500 border-slate-200'}
                  >
                    {product.availability === 'disponible' 
                      ? (isUSA ? 'Available' : 'Disponible') 
                      : (isUSA ? 'Unavailable' : 'No disponible')}
                  </Badge>
                </div>
                
                <div className="mt-3 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1 text-slate-500">
                    <Eye className="w-4 h-4" />
                    {product.views} {isUSA ? 'views' : 'vistas'}
                  </div>
                  {!product.hide_price && product.reference_price && (
                    <span className="font-semibold text-slate-900">
                      ${product.reference_price.toLocaleString()} {product.currency || 'MXN'}/{product.unit || (product.category === 'telas' ? 'Mt' : (isUSA ? 'Unit' : 'pza'))}
                    </span>
                  )}
                </div>

                {product.moq && (
                  <p className="mt-2 text-xs text-slate-500">
                    MOQ: {product.moq.toLocaleString()} {product.unit || (product.category === 'telas' ? 'Mt' : (isUSA ? 'units' : 'pzas'))}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Image Modal for enlarging product images */}
      <ImageModal 
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ isOpen: false, src: '', title: '' })}
        imageSrc={imageModal.src}
        title={imageModal.title}
      />
    </div>
  );
};

export default ProductList;
